import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { styled } from '@mui/material/styles';
import TextField from '@/components/TextField/TextField';
import Select from '@/components/Select/Select';
import Switch from '@/components/Switch/Switch';
import Popper from '@/components/Popper/Popper';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import SaveIcon from '@/assets/save-floppy-disk.svg?react';
import {
  fetchFilters,
  fetchPartners,
  postInvite,
  putUser,
  postUserRoles,
  deleteUserRoles,
  postUserPermissions,
  deleteUserPermissions,
} from '@/lib/api';
import { partnerQueryKeys } from '@/lib/partnerQueryKeys';
import { AxiosError } from 'axios';
import {
  ApiError,
  ContactSchema,
  PartnerSchema,
  UserRole,
} from '@/lib/types';
import { getPermissionIdsByKeys } from '@/lib/permissions';
import useHasUserPermissions from '@/hooks/useHasUserPermissions';
import { getRoleIdByName } from '@/lib/roles';
import useApiErrorHandler from '@/hooks/useApiErrorHandler';
import {
  BASELINE_PERMISSIONS,
  VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
  EDIT_CONTACTS_PERMISSIONS,
} from '@/constants/Permissions';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';

const ContactFormModal = ({
  open,
  onClose,
  onSave,
  partnerId,
  contact,
}: {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  partnerId: number;
  contact?: ContactSchema;
}) => {
  const isEditMode = !!contact;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [additionalPartnerIds, setAdditionalPartnerIds] = useState<
    number[]
  >([]);
  const canManageRoles = useHasUserPermissions([
    'users.manage_roles',
  ]);
  const canManagePermissions = useHasUserPermissions([
    'users.manage_permissions',
  ]);
  const defaultAccessType = canManagePermissions
    ? 'permission'
    : 'role';
  const [accessType, setAccessType] = useState<'role' | 'permission'>(
    defaultAccessType,
  );
  const [role, setRole] = useState<UserRole>('External Viewer');
  const [viewComplianceDocs, setViewComplianceDocs] = useState(false);
  const [editContacts, setEditContacts] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    [key: string]: string;
  }>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [infoPopperOpen, setInfoPopperOpen] = useState(false);
  const infoIconRef = useRef<HTMLDivElement>(null);
  const { handleError: handleApiError } = useApiErrorHandler();

  const { data: filtersData } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPopperReady, setIsPopperReady] = useState(false);
  const [partnerSearchText, setPartnerSearchText] = useState('');
  const partnerSearchParams = partnerSearchText
    ? { search: partnerSearchText }
    : {};

  const handlePopperRef = (el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setIsPopperReady(!!el);
  };

  const {
    data: partnersData,
    isLoading: isLoadingPartners,
    isFetchingNextPage,
  } = useInfiniteScroll<PartnerSchema>({
    fetchFn: fetchPartners,
    queryKey: partnerQueryKeys.infinite(partnerSearchParams),
    enabled: isPopperReady,
    params: partnerSearchParams,
    scrollContainerRef:
      scrollContainerRef as React.RefObject<HTMLElement>,
    pageSize: 20,
    scrollThreshold: 200,
  });

  const subsetPermissionsIds = useMemo(
    () => [
      ...getPermissionIdsByKeys(filtersData?.permissions || [], [
        ...VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
        ...EDIT_CONTACTS_PERMISSIONS,
      ]),
    ],
    [filtersData?.permissions],
  );

  useEffect(() => {
    if (!open) {
      setName('');
      setEmail('');
      setAdditionalPartnerIds([]);
      setAccessType(defaultAccessType);
      setRole('External Viewer');
      setViewComplianceDocs(false);
      setEditContacts(false);
      setFormErrors({});
      setGeneralErrors([]);
      setPartnerSearchText('');
    } else if (open && contact) {
      setName(contact.name);
      setEmail(contact.email);

      const contactPartnerIds =
        contact.partners
          ?.map((p) => p.id)
          .filter((id) => id !== partnerId) || [];
      setAdditionalPartnerIds(contactPartnerIds);

      const hasAdminRole = contact.roles.some(
        (r) => r.name === 'External Admin',
      );
      const hasViewerRole = contact.roles.some(
        (r) => r.name === 'External Viewer',
      );

      if (hasAdminRole || hasViewerRole) {
        setAccessType('role');
        if (hasAdminRole) {
          setRole('External Admin');
        } else {
          setRole('External Viewer');
        }
        setViewComplianceDocs(false);
        setEditContacts(false);
      } else {
        setAccessType('permission');
        setRole('External Viewer');

        const permissionKeys = contact.permissions.map((p) => p.key);
        const hasViewComplianceDocsPermissions = [
          ...BASELINE_PERMISSIONS,
          ...VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
        ].every((p) => permissionKeys.includes(p));

        setViewComplianceDocs(hasViewComplianceDocsPermissions);

        const hasAdminPermissions = [
          ...BASELINE_PERMISSIONS,
          ...EDIT_CONTACTS_PERMISSIONS,
        ].every((p) => permissionKeys.includes(p));

        setEditContacts(hasAdminPermissions);
      }
    }
  }, [contact, open, defaultAccessType, partnerId]);

  const handleError = (error: AxiosError<ApiError>) => {
    const {
      generalErrors: apiGeneralErrors,
      fieldErrors,
      unhandledErrors,
    } = handleApiError(error);

    setFormErrors(fieldErrors);
    setGeneralErrors([...apiGeneralErrors, ...unhandledErrors]);
  };

  const { mutate: mutateCreate, isPending: isCreating } = useMutation(
    {
      mutationFn: postInvite,
      onSuccess: () => {
        onSave();
        handleClose();
      },
      onError: handleError,
    },
  );

  const { mutateAsync: mutatePutAsync, isPending: isUpdatingName } =
    useMutation({
      mutationFn: putUser,
      onError: handleError,
    });

  const {
    mutateAsync: mutatePostRolesAsync,
    isPending: isUpdatingRoles,
  } = useMutation({
    mutationFn: postUserRoles,
    onError: handleError,
  });

  const {
    mutateAsync: mutateDeleteRolesAsync,
    isPending: isDeletingRoles,
  } = useMutation({
    mutationFn: deleteUserRoles,
    onError: handleError,
  });

  const {
    mutateAsync: mutatePostPermissionsAsync,
    isPending: isAddingPermissions,
  } = useMutation({
    mutationFn: postUserPermissions,
    onError: handleError,
  });

  const {
    mutateAsync: mutateDeletePermissionsAsync,
    isPending: isDeletingPermissions,
  } = useMutation({
    mutationFn: deleteUserPermissions,
    onError: handleError,
  });

  const isPending =
    isCreating ||
    isUpdatingName ||
    isUpdatingRoles ||
    isDeletingRoles ||
    isAddingPermissions ||
    isDeletingPermissions;

  const handleClose = () => {
    setName('');
    setEmail('');
    setAdditionalPartnerIds([]);
    setAccessType('role');
    setRole('External Viewer');
    setViewComplianceDocs(false);
    setEditContacts(false);
    onClose();
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = async () => {
    const newFormErrors: { [key: string]: string } = {};

    if (!name.trim()) {
      newFormErrors.name = 'Contact Name is required';
    }

    if (!email.trim()) {
      newFormErrors.email = 'Contact Email is required';
    } else if (!validateEmail(email)) {
      newFormErrors.email = 'Please enter a valid email address';
    }

    if (Object.keys(newFormErrors).length > 0) {
      setFormErrors(newFormErrors);
      return;
    }

    setFormErrors({});

    if (accessType === 'role') {
      const roleId = getRoleIdByName(filtersData?.roles || [], role);

      if (!roleId) {
        throw new Error('Role not found');
      }

      if (isEditMode && contact && roleId) {
        try {
          const allPartnerIds = [partnerId, ...additionalPartnerIds];
          await mutatePutAsync({
            user_id: contact.id,
            name,
            partner_ids: allPartnerIds,
          });

          const currentRoles = contact.roles || [];
          const rolesToDelete = currentRoles
            .filter((r) => r.name !== role)
            .map((r) =>
              getRoleIdByName(filtersData?.roles || [], r.name),
            )
            .filter((id): id is number => id !== undefined);

          if (rolesToDelete.length > 0) {
            await Promise.all(
              rolesToDelete.map((oldRoleId) =>
                mutateDeleteRolesAsync({
                  user_id: contact.id,
                  role_id: oldRoleId,
                }),
              ),
            );
          }

          await mutatePostRolesAsync({
            user_id: contact.id,
            role_id: roleId,
          });

          onSave();
          handleClose();
        } catch (_error) {
          // Error handled by handleError in mutation
        }
      } else {
        mutateCreate({
          name,
          email,
          partner_ids: [partnerId, ...additionalPartnerIds],
          role_name: role,
        });
      }
    } else {
      const permissionIds: number[] = getPermissionIdsByKeys(
        filtersData?.permissions || [],
        BASELINE_PERMISSIONS,
      );

      if (viewComplianceDocs) {
        permissionIds.push(
          ...getPermissionIdsByKeys(
            filtersData?.permissions || [],
            VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
          ),
        );
      }

      if (editContacts) {
        permissionIds.push(
          ...getPermissionIdsByKeys(
            filtersData?.permissions || [],
            EDIT_CONTACTS_PERMISSIONS,
          ),
        );
      }

      if (isEditMode && contact) {
        try {
          const allPartnerIds = [partnerId, ...additionalPartnerIds];
          await mutatePutAsync({
            user_id: contact.id,
            name,
            partner_ids: allPartnerIds,
          });

          const currentPermissionIds = contact.permissions.map(
            (p) => p.id,
          );
          const permissionsToAdd = permissionIds.filter(
            (id) => !currentPermissionIds.includes(id),
          );
          const permissionsToDelete = currentPermissionIds.filter(
            (id) =>
              !permissionIds.includes(id) &&
              subsetPermissionsIds.includes(id),
          );

          const permissionPromises: Promise<unknown>[] = [];

          if (permissionsToAdd.length > 0) {
            permissionPromises.push(
              mutatePostPermissionsAsync({
                user_id: contact.id,
                permission_ids: permissionsToAdd,
              }),
            );
          }

          if (permissionsToDelete.length > 0) {
            permissionPromises.push(
              mutateDeletePermissionsAsync({
                user_id: contact.id,
                permission_ids: permissionsToDelete,
              }),
            );
          }

          await Promise.all(permissionPromises);

          onSave();
          handleClose();
        } catch (_error) {
          // Error handled by handleError in mutation
        }
      } else {
        mutateCreate({
          name,
          email,
          permission_ids: permissionIds,
          partner_ids: [partnerId, ...additionalPartnerIds],
        });
      }
    }
  };

  const partnerOptions =
    partnersData
      ?.filter?.((partner) => partner.id !== partnerId)
      .map((partner) => ({
        id: partner.id,
        value: partner.id,
        label: partner.name,
      })) || [];

  const selectedPartnerOptions = useMemo(() => {
    if (!isEditMode || !contact) return undefined;
    return contact.partners
      ?.filter((p) => p.id !== partnerId)
      .map((partner) => ({
        id: partner.id,
        value: partner.id,
        label: partner.name,
      }));
  }, [isEditMode, contact, partnerId]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        isEditMode ? (
          <FormattedMessage
            id="editContact"
            defaultMessage="Edit Contact"
          />
        ) : (
          <FormattedMessage
            id="addContact"
            defaultMessage="Add Contact"
          />
        )
      }
      actions={[
        <Button
          key="close"
          variant="text"
          onClick={handleClose}
          disabled={isPending}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="save"
          variant="contained"
          onClick={handleSave}
          disabled={isPending}
          loading={isPending}
          aria-label="Save Button"
        >
          <SaveIcon />
          <FormattedMessage id="save" defaultMessage="Save" />
        </Button>,
      ]}
      generalErrors={generalErrors}
    >
      <Label>
        <FormattedMessage
          id="contactName"
          defaultMessage="Contact Name"
        />
        <Asterix>*</Asterix>
      </Label>
      <StyledTextField
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (formErrors.name) {
            setFormErrors((prev) => {
              const { name: _name, ...rest } = prev;
              return rest;
            });
          }
        }}
        placeholder="Type Contact Name"
        variant="outlined"
        fullWidth
        error={!!formErrors.name}
        helperText={formErrors.name || ' '}
      />
      <Label>
        <FormattedMessage
          id="contactEmail"
          defaultMessage="Contact Email"
        />
        <Asterix>*</Asterix>
      </Label>
      <StyledTextField
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (formErrors.email) {
            setFormErrors((prev) => {
              const { email: _email, ...rest } = prev;
              return rest;
            });
          }
        }}
        disabled={isEditMode}
        placeholder="Type Contact Email"
        variant="outlined"
        fullWidth
        error={!!formErrors.email}
        helperText={formErrors.email || ' '}
      />
      <Label>
        <FormattedMessage
          id="additionalPartners"
          defaultMessage="Additional partners"
        />
      </Label>
      <StyledSelect
        ref={handlePopperRef}
        multiple
        value={additionalPartnerIds}
        onChange={(value) =>
          setAdditionalPartnerIds(value as number[])
        }
        loading={isLoadingPartners}
        loadingNextPage={isFetchingNextPage}
        options={partnerOptions}
        selectedOptions={selectedPartnerOptions}
        placeholder="Select additional partners"
        aria-label="Additional partners"
        searchEnabled
        onSearchChange={setPartnerSearchText}
        disableLocalFiltering
      />
      <Label>
        <FormattedMessage
          id="accessType"
          defaultMessage="Access Type"
        />
      </Label>
      {canManagePermissions && canManageRoles && (
        <StyledSelect
          value={accessType}
          onChange={(value) => {
            setAccessType(value as 'role' | 'permission');
            if (value === 'role') {
              setViewComplianceDocs(false);
              setEditContacts(false);
              setRole('External Viewer');
            }
          }}
          options={[
            {
              id: 'permission',
              value: 'permission' as const,
              label: 'Permission',
            },
            {
              id: 'role',
              value: 'role' as const,
              label: 'Role',
            },
          ]}
          placeholder="Select Access Type"
          aria-label="Access Type"
        />
      )}
      {canManageRoles && accessType === 'role' && (
        <>
          <LabelContainer>
            <FormattedMessage id="role" defaultMessage="Role" />
            <InfoIcon
              ref={infoIconRef}
              onMouseEnter={() => setInfoPopperOpen(true)}
              onMouseLeave={() => setInfoPopperOpen(false)}
              aria-label="Role information"
            >
              i
            </InfoIcon>
            <StyledPopper
              anchorEl={infoIconRef.current}
              open={infoPopperOpen}
              onClose={() => setInfoPopperOpen(false)}
              placement="bottom-start"
              offsetY={8}
              aria-label="Role information tooltip"
            >
              <PopperContent>
                <FormattedMessage
                  id="roleInfo"
                  defaultMessage="Admins can view this partner’s page and invite contacts"
                />
              </PopperContent>
            </StyledPopper>
          </LabelContainer>
          <StyledSelect
            value={role}
            onChange={(value) => {
              setRole(value as UserRole);
            }}
            options={[
              {
                id: 'External Viewer',
                value: 'External Viewer' as const,
                label: 'Viewer',
              },
              {
                id: 'External Admin',
                value: 'External Admin' as const,
                label: 'Admin',
              },
            ]}
            placeholder="Select Role"
            aria-label="Role"
          />
        </>
      )}
      {canManagePermissions && accessType === 'permission' && (
        <>
          <Label>
            <FormattedMessage
              id="permissions"
              defaultMessage="Permissions"
            />
          </Label>
          <SwitchContainer>
            <SwitchWrapper>
              <Switch
                checked={viewComplianceDocs}
                onChange={(e) =>
                  setViewComplianceDocs(e.target.checked)
                }
                id="viewComplianceDocs"
                aria-label="View Compliance Documents"
              />
              <SwitchLabel htmlFor="viewComplianceDocs">
                <FormattedMessage
                  id="viewComplianceDocuments"
                  defaultMessage="View Compliance Documents"
                />
              </SwitchLabel>
            </SwitchWrapper>
            <SwitchWrapper>
              <Switch
                checked={editContacts}
                onChange={(e) => setEditContacts(e.target.checked)}
                id="editContacts"
                aria-label="Edit Contacts"
              />
              <SwitchLabel htmlFor="editContacts">
                <FormattedMessage
                  id="editContacts"
                  defaultMessage="Edit Contacts"
                />
              </SwitchLabel>
            </SwitchWrapper>
          </SwitchContainer>
        </>
      )}
    </Modal>
  );
};

const Label = styled('div')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const LabelContainer = styled('div')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const InfoIcon = styled('div')(({ theme }) => ({
  width: '14px',
  height: '14px',
  borderRadius: '50%',
  border: `1px solid ${theme.palette.grey600}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 600,
  color: theme.palette.grey600,
  userSelect: 'none',
  flexShrink: 0,
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  background: theme.palette.white,
  borderRadius: theme.radii.xs,
  boxShadow:
    '0px 6px 14px -6px rgba(19, 25, 39, 0.12), 0px 10px 32px -4px rgba(19, 25, 39, 0.10)',
  padding: theme.spacing(1.5),
  maxWidth: '300px',
  zIndex: 1300,
}));

const PopperContent = styled('div')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: theme.palette.textPrimaryBlack,
}));

const Asterix = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
}));

const StyledTextField = styled(TextField)({
  marginTop: 0,
  marginBottom: 11,
});

const StyledSelect = styled(Select)({
  marginTop: 0,
  marginBottom: 11,
});

const SwitchContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const SwitchWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const SwitchLabel = styled('label')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  cursor: 'pointer',
  userSelect: 'none',
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(1),
}));

export default ContactFormModal;
